import { createSlice } from "@reduxjs/toolkit";
import axios from "src/utils/axios";

// Slice del banco de días compensatorios por festivos.
// Endpoint base: /api/employeeCompensationDay
// Los GET disparan sync automático en backend (-60d/+30d) — el front
// solo pinta lo que llega.

const initialState = {
  isLoading: false,
  error: null,
  daysByEmployee: {},     // { [employeeId]: DtoEmployeeCompensationDayGet[] }
  balanceByEmployee: {},  // { [employeeId]: { earned, used, available } }
};

const slice = createSlice({
  name: "compensationDays",
  initialState,
  reducers: {
    startLoading(state) { state.isLoading = true; state.error = null; },
    stopLoading(state) { state.isLoading = false; },
    hasError(state, action) { state.isLoading = false; state.error = action.payload; },

    setDays(state, action) {
      const { employeeId, days } = action.payload;
      state.daysByEmployee[employeeId] = days || [];
      state.isLoading = false;
    },
    setBalance(state, action) {
      const { employeeId, balance } = action.payload;
      state.balanceByEmployee[employeeId] = balance;
    },
    upsertDay(state, action) {
      const d = action.payload;
      const list = state.daysByEmployee[d.employeeId] || [];
      const idx = list.findIndex((x) => x.id === d.id);
      if (idx >= 0) list[idx] = d;
      else list.unshift(d);
      state.daysByEmployee[d.employeeId] = list;
    },
    removeDay(state, action) {
      const { employeeId, id } = action.payload;
      const list = state.daysByEmployee[employeeId] || [];
      state.daysByEmployee[employeeId] = list.filter((x) => x.id !== id);
    },
  },
});

export default slice.reducer;
export const { setDays, setBalance, upsertDay, removeDay } = slice.actions;

// ── Thunks ──────────────────────────────────────────────────────────

export function getCompensationDays(employeeId, includeUsed = true) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());
    try {
      const res = await axios.get(
        `/employeeCompensationDay/byEmployee/${employeeId}?includeUsed=${includeUsed}`,
      );
      dispatch(slice.actions.setDays({ employeeId, days: res.data || [] }));
      return res.data;
    } catch (err) {
      dispatch(slice.actions.hasError(err?.message || "Error cargando compensaciones"));
      throw err;
    }
  };
}

export function getCompensationBalance(employeeId) {
  return async (dispatch) => {
    const res = await axios.get(`/employeeCompensationDay/balance/${employeeId}`);
    dispatch(slice.actions.setBalance({ employeeId, balance: res.data }));
    return res.data;
  };
}

export function markCompensationUsed(payload) {
  return async (dispatch) => {
    const res = await axios.post("/employeeCompensationDay/markUsed", payload);
    dispatch(slice.actions.upsertDay(res.data));
    return res.data;
  };
}

export function unmarkCompensation(id, employeeId) {
  return async (dispatch) => {
    await axios.post(`/employeeCompensationDay/unmark/${id}`);
    // Refresca banco entero para reflejar cambio limpio.
    if (employeeId) await dispatch(getCompensationDays(employeeId, true));
  };
}

export function deleteCompensation(employeeId, id) {
  return async (dispatch) => {
    await axios.delete(`/employeeCompensationDay/${id}`);
    dispatch(slice.actions.removeDay({ employeeId, id }));
  };
}

export function syncCompensations(employeeId, from, to) {
  return async (dispatch) => {
    const res = await axios.post("/employeeCompensationDay/sync", {
      employeeId,
      from: from || null,
      to: to || null,
    });
    // Tras sync, refresca banco + balance.
    await dispatch(getCompensationDays(employeeId, true));
    await dispatch(getCompensationBalance(employeeId));
    return res.data;
  };
}
